/**
 * Shared Constants and Enums
 * Following constitution requirements for SureBet platform
 *
 * This file contains all shared constants, enums, and static values
 * used across the ArbiWin application modules.
 */

// Re-export new Constants from modules/utils for unified access
import {
  API_ENDPOINTS,
  API_TIMEOUTS,
  REFRESH_INTERVALS,
  ERROR_MESSAGES as NEW_ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from '../modules/utils';

/**
 * Error Types (matching errorHandler.js)
 */
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  SUBSCRIPTION: 'SUBSCRIPTION_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS_CODES = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  PAYMENT_REQUIRED: 402, // Subscription required
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503
};

/**
 * User Roles and Permissions
 */
export const USER_ROLES = {
  USER: 'User',
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin'
};

export const PERMISSIONS = {
  VIEW_SUREBETS: 'view_surebets',
  USE_CALCULATOR: 'use_calculator',
  MANAGE_SUBSCRIPTION: 'manage_subscription',
  ADMIN_USERS: 'admin_users',
  ADMIN_SUBSCRIPTIONS: 'admin_subscriptions',
  SYSTEM_ADMIN: 'system_admin'
};

/**
 * Subscription Status
 */
export const SUBSCRIPTION_STATUS = {
  INACTIVE: 'inactive',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  PENDING: 'pending'
};

/**
 * Surebet Market Types (from constitution MARGIN_TYPES_MAPPING)
 */
export const MARGIN_TYPES = {
  MATCH_RESULT: 'margin1X2',
  OVER_UNDER_2_5: 'marginOverUnder2AndHalf',
  BOTH_SCORE: 'marginBothScore',
  DOUBLE_CHANCE: 'marginDoubleChance',
  ONE_X_VS_2: 'margin1Xvs2',
  X2_VS_1: 'marginX2vs1',
  TWELVE_VS_X: 'margin12vsX',
  OVER_UNDER_1_5: 'marginOverUnder1AndHalf',
  OVER_UNDER_3_5: 'marginOverUnder3AndHalf'
};

/**
 * Coefficient Types (from constitution)
 */
export const COEFFICIENT_TYPES = {
  // 1X2 Market
  HOME_WIN: 'Coef1',
  DRAW: 'CoefX',
  AWAY_WIN: 'Coef2',

  // Over/Under Markets
  OVER_1_5: 'CoefGoalOver1AndHalf',
  UNDER_1_5: 'CoefGoalUnder1AndHalf',
  OVER_2_5: 'CoefGoalOver2AndHalf',
  UNDER_2_5: 'CoefGoalUnder2AndHalf',
  OVER_3_5: 'CoefGoalOver3AndHalf',
  UNDER_3_5: 'CoefGoalUnder3AndHalf',

  // Both Teams to Score
  BOTH_SCORE_YES: 'CoefBothScoreYes',
  BOTH_SCORE_NO: 'CoefBothScoreNo',

  // Double Chance
  HOME_OR_DRAW: 'Coef1X',
  DRAW_OR_AWAY: 'CoefX2',
  HOME_OR_AWAY: 'Coef12',

  // Goal/No Goal
  YES_GOAL: 'CoefYesGoal',
  NO_GOAL: 'CoefNoGoal'
};

/**
 * Bookmaker Identifiers (from constitution)
 */
export const BOOKMAKERS = {
  BETANO: 'betano',
  BET365: 'bet365',
  WINBET: 'winbet',
  BETWILD: 'betwild',
  SLYBET: 'slybet',
  BWIN: 'bwin',
  EFBET: 'efbet',
  INBET: 'inbet',
  ALPHAWIN: 'alphawin',
  SESAME: 'sesame',
  ELITBET: 'elitbet',
  BETHUB: 'bethub',
  BET_BG: 'bet',
  TOPWIN: 'topwin',
  MRBIT: 'mrbit',
  ADMIRALBET: 'admiralbet',
  BG8888: 'bg8888',
  PALMSBET: 'palmsbet',
  EVERBET: 'everbet',
  MAGICBET: 'magicbet',
  BETVAM: 'betvam',
  QBET: 'qbet',
  IMPERIABET: 'imperiabet'
};

/**
 * Sports Types
 */
export const SPORTS = {
  FOOTBALL: 'Football',
  BASKETBALL: 'Basketball',
  TENNIS: 'Tennis',
  VOLLEYBALL: 'Volleyball',
  HANDBALL: 'Handball',
  ICE_HOCKEY: 'Ice Hockey'
};

/**
 * Languages
 */
export const LANGUAGES = {
  BULGARIAN: 'bg',
  ENGLISH: 'en'
};

/**
 * Local Storage Keys
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'arbiwin_auth_token',
  REFRESH_TOKEN: 'arbiwin_refresh_token',
  USER_DATA: 'arbiwin_user_data',
  LANGUAGE: 'arbiwin_language',
  THEME: 'arbiwin_theme',
  BOOKMAKER_FILTERS: 'arbiwin_bookmaker_filters',
  USER_PREFERENCES: 'arbiwin_user_preferences',
  LAST_REFRESH: 'arbiwin_last_refresh',
  CALCULATOR_HISTORY: 'arbiwin_calculator_history'
};

/**
 * Event Names for Custom Events
 */
export const EVENTS = {
  AUTH_LOGIN_SUCCESS: 'auth:loginSuccess',
  AUTH_LOGOUT: 'auth:logout',
  AUTH_TOKEN_EXPIRED: 'auth:tokenExpired',
  AUTH_REQUIRED: 'auth:required',

  SUBSCRIPTION_ACTIVATED: 'subscription:activated',
  SUBSCRIPTION_EXPIRED: 'subscription:expired',
  SUBSCRIPTION_REQUIRED: 'subscription:required',

  SUREBET_DATA_UPDATED: 'surebet:dataUpdated',
  SUREBET_REFRESH_STARTED: 'surebet:refreshStarted',
  SUREBET_REFRESH_COMPLETED: 'surebet:refreshCompleted',

  CALCULATOR_OPENED: 'calculator:opened',
  CALCULATOR_CALCULATED: 'calculator:calculated',

  LANGUAGE_CHANGED: 'language:changed',
  THEME_CHANGED: 'theme:changed',

  ERROR_OCCURRED: 'error:occurred',
  ERROR_RESOLVED: 'error:resolved'
};

/**
 * UI Component States
 */
export const COMPONENT_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
  EMPTY: 'empty'
};

/**
 * Animation Duration Constants
 */
export const ANIMATION_DURATION = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  VERY_SLOW: 1000
};

/**
 * Breakpoint Constants (matching responsive.css)
 */
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  XXL: 1536
};

/**
 * Validation Constants
 */
export const VALIDATION_RULES = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_REGEX: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  STAKE_MIN: 1.0,
  STAKE_MAX: 10000.0,
  MARGIN_MIN: 0.01 // 1%
};

/**
 * Date and Time Constants
 */
export const DATE_TIME = {
  TIMEZONE: 'Europe/Sofia',
  DATE_FORMAT_BG: 'DD.MM.YYYY',
  DATE_FORMAT_EN: 'MM/DD/YYYY',
  TIME_FORMAT: 'HH:mm',
  DATETIME_FORMAT_BG: 'DD.MM.YYYY HH:mm',
  DATETIME_FORMAT_EN: 'MM/DD/YYYY HH:mm'
};

/**
 * API Request Timeouts
 */
export const TIMEOUTS = {
  API_REQUEST: 10000,
  AUTHENTICATION: 15000,
  FILE_UPLOAD: 30000,
  LONG_POLLING: 60000
};

/**
 * Pagination Constants
 */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  MIN_PAGE_SIZE: 5
};

/**
 * Feature Flags for Phase Control
 */
export const FEATURE_FLAGS = {
  // Phase 1 features (always enabled)
  PHASE_1_COMPLETE: true,

  // Phase 2 features (disabled until Phase 2)
  AI_INTEGRATION: false,
  AI_ANALYSIS: false,
  AI_RECOMMENDATIONS: false,
  ENHANCED_PREDICTIONS: false,
  PERSONALIZED_INSIGHTS: false,

  // Development features
  DEBUG_PANEL: process.env.NODE_ENV === 'development',
  MOCK_DATA: process.env.REACT_APP_USE_MOCK_DATA === 'true',
  PERFORMANCE_MONITORING: process.env.REACT_APP_PERFORMANCE_MONITORING !== 'false'
};

/**
 * Application Limits
 */
export const LIMITS = {
  MAX_CONCURRENT_REQUESTS: 10,
  MAX_RETRY_ATTEMPTS: 3,
  MAX_LOGIN_ATTEMPTS: 5,
  MAX_ERROR_LOGS: 100,
  MAX_CALCULATOR_HISTORY: 50,
  MAX_SUREBET_DISPLAY: 500
};

/**
 * Toast/Notification Types
 */
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

/**
 * Modal Types
 */
export const MODAL_TYPES = {
  CONFIRMATION: 'confirmation',
  ERROR: 'error',
  INFO: 'info',
  CALCULATOR: 'calculator',
  SUBSCRIPTION: 'subscription',
  FILTERS: 'filters'
};

/**
 * Loading States
 */
export const LOADING_STATES = {
  INITIAL: 'initial',
  LOADING: 'loading',
  REFRESHING: 'refreshing',
  SUBMITTING: 'submitting',
  RETRYING: 'retrying'
};

// Merge constants - existing constants take precedence, new ones extend functionality
export const API_ENDPOINTS_NEW = API_ENDPOINTS;
export const API_TIMEOUTS_NEW = API_TIMEOUTS;
export const REFRESH_INTERVALS_NEW = REFRESH_INTERVALS;
export const ERROR_MESSAGES_NEW = NEW_ERROR_MESSAGES;
export const SUCCESS_MESSAGES_NEW = SUCCESS_MESSAGES;

export default {
  ERROR_TYPES,
  HTTP_STATUS_CODES,
  USER_ROLES,
  PERMISSIONS,
  SUBSCRIPTION_STATUS,
  MARGIN_TYPES,
  COEFFICIENT_TYPES,
  BOOKMAKERS,
  SPORTS,
  LANGUAGES,
  STORAGE_KEYS,
  EVENTS,
  COMPONENT_STATES,
  ANIMATION_DURATION,
  BREAKPOINTS,
  VALIDATION_RULES,
  DATE_TIME,
  TIMEOUTS,
  PAGINATION,
  FEATURE_FLAGS,
  LIMITS,
  TOAST_TYPES,
  MODAL_TYPES,
  LOADING_STATES,
  // New constants from modules/utils
  API_ENDPOINTS: API_ENDPOINTS,
  API_TIMEOUTS: API_TIMEOUTS,
  REFRESH_INTERVALS: REFRESH_INTERVALS,
  ERROR_MESSAGES: NEW_ERROR_MESSAGES,
  SUCCESS_MESSAGES: SUCCESS_MESSAGES
};
